import { FileDoneOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, List, Row, Statistic, Typography } from 'antd';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dashboardApi } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { queryKeys } from '../api/queryKeys';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStoreContext } from '../context/StoreContext';
import { usePendingShipmentsCount } from '../hooks/usePendingShipmentsCount';
import { skladDebtColor, storeBalanceColor } from '../lib/balanceColors';
import { formatKopeks } from '../lib/money';

function MoneyStat({ title, kopeks, color }: { title: string; kopeks: number; color?: string }): JSX.Element {
  return <Statistic title={title} value={formatKopeks(kopeks)} valueStyle={{ fontSize: 20, color }} />;
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const { isStoreRestricted } = useAuth();
  const navigate = useNavigate();
  const { storeId, storeIds } = useStoreContext();
  const pendingShipments = usePendingShipmentsCount();

  const query = useQuery({
    queryKey: queryKeys.dashboard({ storeId, storeIds }),
    queryFn: () => dashboardApi.view({ storeId, storeIds }),
  });

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const data = query.data;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {pendingShipments > 0 ? (
        <Alert
          type="warning"
          showIcon
          icon={<FileDoneOutlined />}
          message={t('shipments.pendingBanner', { count: pendingShipments })}
          action={
            <Button size="small" type="primary" onClick={() => navigate('/shipments')}>
              {t('shipments.pendingBannerAction')}
            </Button>
          }
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/shipments')}
        />
      ) : null}
      <Row gutter={[12, 12]}>
        <Col xs={isStoreRestricted ? 24 : 12} md={isStoreRestricted ? 24 : 12}>
          <Card size="small">
            <MoneyStat
              title={t('dashboard.storesOwe')}
              kopeks={data.totalStoreBalance}
              color={storeBalanceColor(data.totalStoreBalance)}
            />
          </Card>
        </Col>
        {/* Sklad debt is whole-business — not shown to a store-restricted USER. */}
        {!isStoreRestricted ? (
          <Col xs={12} md={12}>
            <Card size="small">
              <MoneyStat
                title={t('dashboard.skladDebt')}
                kopeks={data.totalSkladDebt}
                color={skladDebtColor(data.totalSkladDebt)}
              />
            </Card>
          </Col>
        ) : null}
      </Row>

      <Card size="small" title={t('dashboard.monthTitle')}>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <MoneyStat title={t('dashboard.issued')} kopeks={data.month.issued} />
          </Col>
          <Col xs={12} md={6}>
            <MoneyStat title={t('dashboard.paid')} kopeks={data.month.paid} />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {t('payMethod.breakdownShort', {
                cash: formatKopeks(data.paidByMethod.cash),
                terminal: formatKopeks(data.paidByMethod.terminal),
              })}
              {data.paidByMethod.unspecified > 0
                ? ` · ${t('payMethod.unspecifiedShort', {
                    amount: formatKopeks(data.paidByMethod.unspecified),
                  })}`
                : ''}
            </Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <MoneyStat title={t('dashboard.purchases')} kopeks={data.month.purchases} />
          </Col>
          <Col xs={12} md={6}>
            <MoneyStat title={t('dashboard.expenses')} kopeks={data.month.expenses} />
          </Col>
        </Row>
      </Card>

      <Card size="small" title={t('dashboard.storeList')}>
        {data.stores.length === 0 ? (
          <EmptyBlock description={t('dashboard.empty')} />
        ) : (
          <List
            dataSource={data.stores}
            renderItem={(store) => (
              <List.Item
                onClick={() => navigate(`/stores/${store.id}`)}
                style={{ cursor: 'pointer', paddingInline: 4 }}
                extra={<RightOutlined style={{ color: '#bbb' }} />}
              >
                <List.Item.Meta title={store.name} />
                <Typography.Text
                  strong
                  style={{ marginInlineEnd: 12, fontSize: 16, color: storeBalanceColor(store.balance) }}
                >
                  {formatKopeks(store.balance)}
                </Typography.Text>
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
}
